import React, {useEffect, useState} from "react";
import {Prompt} from "react-router-dom";
import {Button, CircularProgress, IconButton} from "@mui/material";
import {NavigateBefore as PrevIcon, NavigateNext as NextIcon} from "@mui/icons-material";
import {ConfirmDeleteDialog, VerticalDivider} from "@atttomyx/react-components";
import {ContactForm} from "../forms";
import {nodeService} from "../../services";
import {arrays, forms, strings} from "@atttomyx/shared-utils";
import {nodeUtils} from "../../utils";
import {verbiage} from "@atttomyx/shared-constants";
import "./editContactPage.css";

const isValid = (contact) => strings.isNotBlank(contact.id);

const toData = (contact) => {
    return {
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        phone: contact.phone,
        leadSource: contact.leadSource,
        valid: isValid(contact),
    }
};

const EditContactPage = (props) => {
    const { snackbar, match, contacts, filters, onSave, onPrev, onNext } = props;
    const [ contact, setContact ] = useState({})
    const [ data, setData ] = useState(toData({}));
    const [ saving, setSaving ] = useState(false);
    const [ showDelete, setShowDelete ] = useState(false);
    const [ prevId, setPrevId ] = useState(null);
    const [ nextId, setNextId ] = useState(null);

    const loadContact = (contactId) => {
        const filtered = filters.filter(contacts);
        const contact = arrays.findEntity(filtered, contactId) || {};
        const ids = arrays.getPrevNextIds(filtered, contact);

        setContact(nodeUtils.decorateNode(contact));
        setPrevId(ids.prevId);
        setNextId(ids.nextId);
    };

    const redirectToPrev = () => {
        onPrev(prevId);
        loadContact(prevId);
    };

    const redirectToNext = () => {
        onNext(nextId);
        loadContact(nextId);
    };

    useEffect(() => {
        const contactId = match.params.id;

        loadContact(contactId);
    }, []);

    useEffect(() => {
        setData(toData(contact));
    }, [contact]);

    const resetForm = () => {
        setData(toData(contact));
    };

    const cleanForm = () => {
        setData(forms.cleanData(data));
    };

    const submitForm = () => {
        setSaving(true);

        const modified = {
            firstName: data.firstName,
            lastName: data.lastName,
            emails: [],
            phones: [],
            metadata: {},
        };

        if (data.email) {
            modified.emails = [{
                label: "Primary",
                value: data.email,
            }];
        }

        if (data.phone) {
            modified.phones = [{
                label: "Primary",
                value: data.phone,
            }];
        }

        if (data.leadSource) {
            modified.metadata = {
                leadSource: data.leadSource,
            };
        }

        const success = (contact) => {
            cleanForm();
            setSaving(false);

            snackbar.setSuccess("Contact saved");
            setContact(nodeUtils.decorateNode(contact));
            onSave(contact);
        };

        const failure = (err) => {
            setSaving(false);

            snackbar.setError(err);
        };

        nodeService.updateNode(contact.id, modified, success, failure);
    };

    const valid = data.valid;
    const modified = forms.differ(toData(contact), data);

    return saving ?
        <div className="edit-contact-page">
            <CircularProgress size="80px"/>
        </div> :
        <div className="edit-contact-page">
            <div className="cards">
                <div className="card">
                    <ContactForm
                        snackbar={snackbar}
                        data={data}
                        onChange={setData}
                        showImage={true}
                        showPhone={true}
                        requirePhone={true}
                    />
                </div>
            </div>
            <div className="actions">
                <IconButton color="secondary" title="Previous contact"
                            disabled={!prevId}
                            onClick={redirectToPrev}>
                    <PrevIcon/>
                </IconButton>
                <IconButton color="secondary" title="Next contact"
                            disabled={!nextId}
                            onClick={redirectToNext}>
                    <NextIcon/>
                </IconButton>
                <VerticalDivider/>
                <Button color="secondary" variant="text"
                        disabled={!modified}
                        onClick={resetForm}>
                    Undo
                </Button>
                <Button color="primary" size="large"
                        disabled={!valid || !modified || !contact.id}
                        onClick={submitForm}>
                    Save
                </Button>
            </div>
            {showDelete && contact ?
                <ConfirmDeleteDialog
                    snackbar={snackbar}
                    title={contact.name}
                    onCancel={() => {
                        setShowDelete(false);
                    }}
                    onDelete={(contactId) => {
                        setShowDelete(false);
                    }}
                    deleter={(success, failure) => nodeService.deleteNode(contact.id, success, failure)}
                /> : null}
            <Prompt when={modified} message={verbiage.UNSAVED_CHANGES}/>
        </div>
}

export default EditContactPage;
